import React, { useState } from "react";
import {
  Form,
  Input,
  Button,
  message,
  Card,
  Spin,
  Row,
  Col,
  Typography,
  Progress,
  Select as AntdSelect,
} from "antd";
import {
  MailOutlined,
  PhoneOutlined,
  LockOutlined,
  UserOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../utils/axiosInstance";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

const { Title, Text } = Typography;
const { Option } = AntdSelect;

const roleOptions = [
  { value: "admin", label: "Admin" },
  { value: "super admin", label: "Super Admin" },
  { value: "sales", label: "Sales" },
  { value: "deliveries", label: "Deliveries" },
  // added roles
  { value: "production manager", label: "Production Manager" },
  { value: "production admin", label: "Production Admin" },
];

const roleColors = {
  admin: "orange",
  "super admin": "red",
  sales: "green",
  deliveries: "purple",
  // colors for new roles
  "production manager": "indigo",
  "production admin": "blue", // ← dot color added for the last role
};

const AddEmployee = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  const evaluatePasswordStrength = (password) => {
    let score = 0;
    if (!password) return 0;
    if (/[a-z]/.test(password)) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[!@#$%^&*]/.test(password)) score++;
    return score * 25;
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.post("/admin/create-user", {
        full_name: values.full_name,
        email: values.email,
        phone_number: values.phone_number,
        role: values.role,
        password: values.password,
      });
      if (data.success) {
        message.success("Employee added successfully!");
        form.resetFields();
        navigate("/requests", { state: { newEmployee: data.user } });
      } else {
        message.error(data.message || "Failed to add employee.");
      }
    } catch (err) {
      const msg = err.response?.data?.message || "Employee addition failed.";
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-3 text-gray-600 font-medium">Adding employee...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 lg:p-6">
      <div className="max-w-2xl mx-auto">
        <Card className="border-0 shadow-sm rounded-2xl bg-white overflow-hidden">
          <div className="p-6 lg:p-8">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-50 rounded-2xl mb-4">
                <TeamOutlined className="text-2xl text-blue-600" />
              </div>
              <Title level={2} className="text-gray-900 mb-2">
                Add New Role
              </Title>
            </div>

            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              initialValues={{ role: undefined }}
              className="space-y-6"
            >
              {/* Personal Info */}
              <div className="bg-gray-50 p-4 rounded-xl">
                <Title level={4} className="text-gray-900 mb-4 flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  Personal Info
                </Title>
                <Row gutter={[16, 8]}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="full_name"
                      label="Full Name"
                      rules={[
                        { required: true, message: "Please enter full name!" },
                        {
                          pattern: /^\p{L}+(\s+\p{L}+)*$/u,
                          message: "Name can only contain letters and spaces",
                        },
                      ]}
                    >
                      <Input
                        prefix={<UserOutlined className="text-gray-400" />}
                        placeholder="Full name"
                        className="h-11 rounded-lg border-gray-300 hover:border-blue-500 focus:border-blue-500"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="email"
                      label="Email"
                      rules={[
                        { required: true, message: "Please enter email!" },
                        { type: "email", message: "Enter a valid email!" },
                      ]}
                    >
                      <Input
                        prefix={<MailOutlined className="text-gray-400" />}
                        placeholder="Email"
                        className="h-11 rounded-lg border-gray-300 hover:border-blue-500 focus:border-blue-500"
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={[16, 8]}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="phone_number"
                      label="Phone Number"
                      rules={[
                        { required: true, message: "Please enter phone number!" },
                        {
                          validator: (_, value) =>
                            value && value.replace(/\D/g, "").length >= 7
                              ? Promise.resolve()
                              : Promise.reject("Valid phone number required!"),
                        },
                      ]}
                    >
                      <PhoneInput
                        country={"ie"}
                        value={form.getFieldValue("phone_number")}
                        onChange={(phone) => form.setFieldsValue({ phone_number: phone })}
                        enableSearch
                        inputStyle={{
                          width: "100%",
                          height: "44px",
                          borderRadius: "8px",
                          border: "1px solid #d1d5db",
                          fontSize: "14px",
                        }}
                        buttonStyle={{
                          borderRadius: "8px 0 0 8px",
                          background: "#f9fafb",
                          border: "1px solid #d1d5db",
                          borderRight: "none",
                        }}
                        dropdownStyle={{
                          zIndex: 1000,
                          borderRadius: "8px",
                        }}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              {/* Employee Role */}
              <div className="bg-gray-50 p-4 rounded-xl">
                <Title level={4} className="text-gray-900 mb-4 flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  Employee Role
                </Title>
                <Form.Item
                  name="role"
                  label="Role"
                  rules={[{ required: true, message: "Please select a role!" }]}
                >
                  <AntdSelect
                    placeholder="Select role"
                    onChange={(value) => form.setFieldsValue({ role: value })}
                    className="w-full h-11 rounded-lg border-gray-300 hover:border-blue-500"
                    dropdownClassName="rounded-lg"
                  >
                    {roleOptions.map((r) => (
                      <Option key={r.value} value={r.value}>
                        <div className="flex items-center space-x-2">
                          <div className={`w-2 h-2 bg-${roleColors[r.value]}-500 rounded-full`}></div>
                          <span>{r.label}</span>
                        </div>
                      </Option>
                    ))}
                  </AntdSelect>
                </Form.Item>
              </div>

              {/* Security */}
              <div className="bg-gray-50 p-4 rounded-xl">
                <Title level={4} className="text-gray-900 mb-4 flex items-center gap-2">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                  Security
                </Title>
                <Row gutter={[16, 8]}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="password"
                      label="Password"
                      rules={[
                        { required: true, min: 8, message: "At least 8 characters!" },
                        {
                          pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*]).{8,}$/,
                          message: "Must include upper, lower, number & special char",
                        },
                      ]}
                    >
                      <Input.Password
                        prefix={<LockOutlined className="text-gray-400" />}
                        placeholder="Password"
                        onChange={(e) =>
                          setPasswordStrength(evaluatePasswordStrength(e.target.value))
                        }
                        className="h-11 rounded-lg border-gray-300 hover:border-blue-500 focus:border-blue-500"
                      />
                    </Form.Item>
                    {form.getFieldValue("password") && (
                      <div className="space-y-2">
                        <Progress
                          percent={passwordStrength}
                          showInfo={false}
                          strokeColor={
                            passwordStrength < 50
                              ? "#ef4444"
                              : passwordStrength < 100
                              ? "#f59e0b"
                              : "#10b981"
                          }
                          className="mb-1"
                        />
                        <Text
                          className={`text-sm font-medium ${
                            passwordStrength < 50
                              ? "text-red-600"
                              : passwordStrength < 100
                              ? "text-yellow-600"
                              : "text-green-600"
                          }`}
                        >
                          {passwordStrength < 50
                            ? "Weak"
                            : passwordStrength < 100
                            ? "Medium"
                            : "Strong"}
                        </Text>
                      </div>
                    )}
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="confirm_password"
                      label="Confirm Password"
                      dependencies={["password"]}
                      rules={[
                        { required: true, message: "Please confirm password!" },
                        ({ getFieldValue }) => ({
                          validator(_, v) {
                            return v === getFieldValue("password")
                              ? Promise.resolve()
                              : Promise.reject("Passwords do not match!");
                          },
                        }),
                      ]}
                    >
                      <Input.Password
                        prefix={<LockOutlined className="text-gray-400" />}
                        placeholder="Confirm password"
                        className="h-11 rounded-lg border-gray-300 hover:border-blue-500 focus:border-blue-500"
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 mt-4">
                  <div className="text-xs text-blue-700">
                    <strong>Password Requirements:</strong> Minimum 8 characters with uppercase, lowercase, number, and special character
                  </div>
                </div>
              </div>

              <Form.Item className="mb-0">
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  className="w-full h-12 bg-blue-600 hover:bg-blue-700 border-0 rounded-xl text-lg font-semibold shadow-sm transition-colors"
                >
                  Add New Role
                </Button>
              </Form.Item>
            </Form>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AddEmployee;
